import React, { useEffect, useState } from 'react';
import i18n from 'i18next';
import moment from 'moment';
import { useTranslation } from 'react-i18next';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { ProtectedRoute } from '../authentication/ProtectedRoute';
import { AuthService } from '../authentication/service';
import { UserRoles } from '../enum/UserRoles';
import LoadingScreen from '../shared/components/LoadingScreen';
import { useSearchQuery } from '../shared/hooks/useSearchQuery';
import { actions } from '../store/authentication/authRedux';
import { EnumService } from '../store/enums/enumService';
import enumsSlice from '../store/enums/enumsRedux';
import registrationSlice from '../store/register/brand/registrationSlice';
import { IAllStates } from '../store/rootReducer';
import { ErrorToast } from '../utils/toasters';
import { AgencyRoutes } from './agency/routes';
import { BrandRoutes } from './brand/routes';
import { InfluencerRoutes } from './influencer/routes';
import GlobalPreferencesModal from './shared/user/components/GlobalPreferencesModal';
import { UserService } from './shared/user/service';
import { SuspendedUserModal } from '../shared/components/alert/SuspendedUserModal';
import { selectedCampaignSlice } from '../store/brand/activeCampaignSlice';
import { DEFAULT_DOCUMENT_TITLE } from '../utils/constants';
import { AppReloadVersionModal } from '../shared/components/modal/AppReloadVersionModal';
import { Route, Routes, useLocation, useNavigate } from 'react-router-dom';
import { setNavigate } from '../utils/helpers/navigation';
import { Impersonate } from './shared/impersonate';
import { GlobalDiscoveryRoutes } from './brand/global-discovery/routes';
import { bugCatchEnabled } from '../bugcatch';
import BugCatch from 'bugcatch-sdk';
import { hasOwnSkeleton } from '../shared/utils/skeletonRoutes';

function App() {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const location = useLocation();
    const token = localStorage.getItem('token');
    const lang = useSearchQuery('lang');
    const { user } = useSelector((state: IAllStates) => state.auth, shallowEqual);
    const { isLoading } = useSelector((state: IAllStates) => state.loading);
    const [isGlobalPreferencesOpened, setIsGlobalPreferencesOpened] = useState(false);
    const [showSuspendedModal, setShowSuspendedModal] = useState(false);
    const { activeCampaignId } = useSelector((state: IAllStates) => state.selectedCampaign, shallowEqual);
    const [whoImpersonate, setWhoImpersonate] = useState(localStorage.getItem('whoImpersonate'));
    const isGlobalDiscovery = user?.company?.package?.globalDiscovery;
    const showGlobalLoader = isLoading && !hasOwnSkeleton(location.pathname);
    useEffect(() => {
        const fetchEnums = async () => {
            try {
                const data = await EnumService.getEnums();
                dispatch(enumsSlice.actions.setEnums(data));
            } catch (error) {
                ErrorToast(error);
            }
        };
        fetchEnums();
    }, [dispatch]);

    useEffect(() => {
        setNavigate(navigate);
    }, [navigate]);

    useEffect(() => {
        const fetchPersonalInfo = async () => {
            if (token) {
                try {
                    const data = await AuthService.getPersonalInfo({
                        campaignId: activeCampaignId,
                        token: JSON.parse(token) ?? '',
                    });
                    bugCatchEnabled &&
                        BugCatch.setUser({
                            id: String(data.id),
                            email: data.email,
                            username: data.username,
                        });
                    dispatch(actions.setUser(data));
                } catch (error) {
                    localStorage.clear();
                    ErrorToast(error);
                } finally {
                    if (activeCampaignId) {
                        dispatch(selectedCampaignSlice.actions.setIsAclLoaded(true));
                    }
                }
            }
        };
        fetchPersonalInfo();
    }, [dispatch, token, activeCampaignId]);

    useEffect(() => {
        const fetchGlobalPreferences = async () => {
            if (user?.id) {
                try {
                    const response = await UserService.getGlobalPreference();
                    const areNotificationsOff =
                        !response.isNotificationByEmailRequested &&
                        !response.isNotificationInBrowserRequested &&
                        response.isUnsubscribed &&
                        !response.isNotificationByMentionRequested;
                    const globalPreferencesPastTime = localStorage.getItem('globalPreferencesPastTime');
                    const today = new Date();
                    const savedDate = globalPreferencesPastTime
                        ? JSON.parse(globalPreferencesPastTime)
                        : new Date(today.setDate(today.getDate() - 31));
                    const diffTime = moment(new Date()).diff(savedDate, 'day');
                    if (areNotificationsOff && diffTime > 29) {
                        setIsGlobalPreferencesOpened(true);
                        localStorage.setItem('globalPreferencesPastTime', JSON.stringify(new Date()));
                    }
                } catch (error) {
                    ErrorToast(error);
                }
            }
            setShowSuspendedModal(user?.isSuspended ?? false);
            return () => setShowSuspendedModal(false);
        };
        fetchGlobalPreferences();
    }, [user?.id]);

    useEffect(() => {
        const languageToSave = lang || user?.locale;
        i18n.changeLanguage(languageToSave);
    }, [user, lang]);

    useEffect(() => {
        document.body.classList.toggle('influencer-side', user?.userType === UserRoles.INFLUENCER);
    }, [user?.userType]);

    useEffect(() => {
        document.title = DEFAULT_DOCUMENT_TITLE;
    }, []);

    // useEffect(() => {
    //   if (!!whoImpersonate) {
    //         dispatch(actions.setIsVerified({ isVerified: true }));
    //         navigate(navigateByRole(user));
    //     }
    // }, [whoImpersonate]);

    useEffect(() => {
        dispatch(registrationSlice.actions.clearRegisterUser());
    }, [dispatch]);

    const renderRoutes = () => {
        switch (user?.userType) {
            case UserRoles.INFLUENCER:
                return <InfluencerRoutes />;
            case UserRoles.AGENCY:
            case UserRoles.AGENCY_MASTER:
                return <AgencyRoutes />;
            case UserRoles.BRAND:
            case UserRoles.BRAND_MASTER:
                return isGlobalDiscovery ? <GlobalDiscoveryRoutes /> : <BrandRoutes />;
            default:
                return <div></div>;
        }
    };

    return (
        <>
            <GlobalPreferencesModal
                show={isGlobalPreferencesOpened}
                errorMessage={t('general.updateNotifications')}
                closeModal={() => setIsGlobalPreferencesOpened(false)}
            />
            <SuspendedUserModal show={showSuspendedModal} />
            <Routes>
                <Route path="/*" element={<ProtectedRoute>{renderRoutes()}</ProtectedRoute>} />
                <Route
                    path="/impersonate/:token/:id/:isAdmin/:adminActiveFirstRoute?/:adminActiveSecondRoute?/:adminActiveThirdRoute?"
                    element={<Impersonate setWhoImpersonate={setWhoImpersonate} />}
                />
            </Routes>
            <ToastContainer />
            {showGlobalLoader && <LoadingScreen />}
            <AppReloadVersionModal />
        </>
    );
}

export default App;
